import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Plus, Trash2, MapPin, Calendar } from "lucide-react";
import { toast } from "sonner";

const API_URL = "/api";

interface StatusUpdate {
    update_id: number;
    status: string;
    location: string;
    description: string;
    map_url: string;
    timestamp: string;
}

const StatusUpdates = () => {
    const navigate = useNavigate();
    const { trackingNumber } = useParams();
    const [loading, setLoading] = useState(true);
    const [updates, setUpdates] = useState<StatusUpdate[]>([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUpdate, setNewUpdate] = useState({
        status: "",
        location: "",
        description: "",
        map_url: "",
    });

    useEffect(() => {
        if (!localStorage.getItem("admin_logged_in")) {
            navigate("/admin");
            return;
        }

        fetchUpdates();
    }, [navigate, trackingNumber]);

    const fetchUpdates = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/status-updates.php?tracking_number=${trackingNumber}`,
                { credentials: "include" }
            );

            const data = await response.json();

            if (data.success) {
                setUpdates(data.data);
            } else {
                toast.error("Failed to load status updates");
            }
        } catch (error) {
            console.error("Fetch error:", error);
            toast.error("Failed to load status updates");
        } finally {
            setLoading(false);
        }
    };

    const handleAddUpdate = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!newUpdate.status || !newUpdate.location) {
            toast.error("Status and location are required");
            return;
        }

        try {
            const response = await fetch(`${API_URL}/status-updates.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    tracking_number: trackingNumber,
                    ...newUpdate,
                }),
            });

            const data = await response.json();

            if (data.success) {
                toast.success("Status update added successfully");
                setNewUpdate({ status: "", location: "", description: "", map_url: "" });
                setShowAddForm(false);
                fetchUpdates();
            } else {
                toast.error(data.error || "Failed to add status update");
            }
        } catch (error) {
            console.error("Add error:", error);
            toast.error("Failed to add status update");
        }
    };

    const handleDeleteUpdate = async (updateId: number) => {
        if (!confirm("Are you sure you want to delete this status update?")) {
            return;
        }

        try {
            const response = await fetch(
                `${API_URL}/status-updates.php?update_id=${updateId}`,
                {
                    method: "DELETE",
                    credentials: "include",
                }
            );

            const data = await response.json();

            if (data.success) {
                toast.success("Status update deleted successfully");
                fetchUpdates();
            } else {
                toast.error(data.error || "Failed to delete status update");
            }
        } catch (error) {
            console.error("Delete error:", error);
            toast.error("Failed to delete status update");
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="border-b bg-white sticky top-0 z-10">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button variant="outline" onClick={() => navigate("/admin/dashboard")}>
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                            <div>
                                <h1 className="text-2xl font-bold">Status Updates</h1>
                                <p className="text-sm text-muted-foreground font-mono">{trackingNumber}</p>
                            </div>
                        </div>
                        <Button onClick={() => setShowAddForm(!showAddForm)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Update
                        </Button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto space-y-6">
                    {/* Add Update Form */}
                    {showAddForm && (
                        <Card className="border-primary">
                            <CardHeader>
                                <CardTitle>Add New Status Update</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleAddUpdate} className="space-y-4">
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="status">Status *</Label>
                                            <Select
                                                value={newUpdate.status}
                                                onValueChange={(value) =>
                                                    setNewUpdate((prev) => ({ ...prev, status: value }))
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select status" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Processing">Processing</SelectItem>
                                                    <SelectItem value="Booked">Booked</SelectItem>
                                                    <SelectItem value="Container Loaded">Container Loaded</SelectItem>
                                                    <SelectItem value="Departed">Departed</SelectItem>
                                                    <SelectItem value="In Transit">In Transit</SelectItem>
                                                    <SelectItem value="On Hold">On Hold</SelectItem>
                                                    <SelectItem value="Port Arrival">Port Arrival</SelectItem>
                                                    <SelectItem value="Customs Clearance">Customs Clearance</SelectItem>
                                                    <SelectItem value="Out for Delivery">Out for Delivery</SelectItem>
                                                    <SelectItem value="Delivered">Delivered</SelectItem>
                                                    <SelectItem value="Exception">Exception</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="location">Location *</Label>
                                            <Input
                                                id="location"
                                                required
                                                placeholder="e.g., Athens Port"
                                                value={newUpdate.location}
                                                onChange={(e) =>
                                                    setNewUpdate((prev) => ({ ...prev, location: e.target.value }))
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="description">Description</Label>
                                        <Textarea
                                            id="description"
                                            placeholder="Additional notes or details"
                                            value={newUpdate.description}
                                            onChange={(e) =>
                                                setNewUpdate((prev) => ({ ...prev, description: e.target.value }))
                                            }
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="map_url">Map URL (Optional)</Label>
                                        <Input
                                            id="map_url"
                                            placeholder="Paste Google Maps embed iframe URL"
                                            value={newUpdate.map_url || ''}
                                            onChange={(e) =>
                                                setNewUpdate((prev) => ({ ...prev, map_url: e.target.value }))
                                            }
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Get embed code from Google Maps → Share → Embed a map, then paste the src URL
                                        </p>
                                    </div>
                                    <div className="flex gap-4">
                                        <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                                            Cancel
                                        </Button>
                                        <Button type="submit">
                                            <Save className="h-4 w-4 mr-2" />
                                            Add Update
                                        </Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {/* Timeline */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Status Timeline ({updates.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {loading ? (
                                <div className="text-center py-12">Loading timeline...</div>
                            ) : updates.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    No status updates yet. Add the first update to start tracking!
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {updates.map((update, index) => (
                                        <div key={update.update_id} className="relative flex gap-4">
                                            {/* Timeline line */}
                                            {index < updates.length - 1 && (
                                                <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-border" />
                                            )}

                                            {/* Timeline dot */}
                                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold z-10">
                                                {updates.length - index}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 pb-6">
                                                <Card>
                                                    <CardContent className="p-4">
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1">
                                                                <div className="flex items-center gap-3 mb-2">
                                                                    <Badge className="text-base px-3 py-1">
                                                                        {update.status}
                                                                    </Badge>
                                                                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                                                        <Calendar className="h-4 w-4" />
                                                                        {formatDate(update.timestamp)}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start gap-2">
                                                                    <MapPin className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                                                                    <div>
                                                                        <p className="font-semibold">{update.location}</p>
                                                                        {update.description && (
                                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                                {update.description}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                variant="destructive"
                                                                onClick={() => handleDeleteUpdate(update.update_id)}
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default StatusUpdates;
